/**
 * MED-1162 — Shared participant/leader display-name helpers for Live Session
 * Delivery surfaces.
 *
 * Participants (and coaches / peer leaders) have a `preferred_name` (formal
 * "Robert" → preferred "Bob"). Every roster surface should label a person by
 * their preferred name when one is set, falling back to the formal first name.
 * Before this story each surface built its own `${first_name} ...` label, so
 * the preferred name never showed. Routing every surface through these helpers
 * keeps the rule (AC1) in one place so it can't drift apart again (Open Q1).
 *
 * PREVENTION — do NOT inline `${first_name}` in a roster/member label on a Live
 * Session Delivery surface. Use {@link getParticipantDisplayName} (the "Bob"
 * value, pair with the last initial → `Preferred L.`) and
 * {@link getParticipantInitials} (avatar initials, AC3). The two administrative
 * surfaces (post-session attendance rows, session-leader picker) instead show
 * the full formal name and append {@link getPreferredParenthetical} as a dimmed
 * `(Bob)` (AC5).
 */

const isSet = (value: string | null | undefined): value is string =>
  typeof value === 'string' && value.trim().length > 0;

/**
 * AC1 — the displayed first name: preferred name when non-empty, else the formal
 * first name. An empty/whitespace preferred name is treated as "not set" so a
 * blank never blanks the label (AC7).
 */
export function getParticipantDisplayName(
  preferredName: string | null | undefined,
  firstName: string | null | undefined,
): string {
  if (isSet(preferredName)) return preferredName.trim();
  return (firstName ?? '').trim();
}

/**
 * AC3 — avatar initials derived from the SAME name the label uses: first letter
 * of (preferred-or-first) + first letter of last name. Accepts either a full
 * `lastName` or an already-derived last initial.
 */
export function getParticipantInitials(
  preferredName: string | null | undefined,
  firstName: string | null | undefined,
  lastNameOrInitial: string | null | undefined,
): string {
  const display = getParticipantDisplayName(preferredName, firstName);
  const first = display.charAt(0);
  const last = (lastNameOrInitial ?? '').trim().charAt(0);
  return (first + last).toUpperCase();
}

/**
 * AC5 — the value to show in the dimmed parenthetical on full-formal-name
 * (administrative) surfaces: the preferred name, but ONLY when it is set AND
 * differs from the formal first name (case-insensitive). Returns null when there
 * should be no parenthetical (no preferred name, or it equals the first name) so
 * callers render no stray empty parens (AC7).
 */
export function getPreferredParenthetical(
  preferredName: string | null | undefined,
  firstName: string | null | undefined,
): string | null {
  if (!isSet(preferredName)) return null;
  const preferred = preferredName.trim();
  if (preferred.toLowerCase() === (firstName ?? '').trim().toLowerCase()) return null;
  return preferred;
}

/**
 * AC5 (plain-string contexts, e.g. a native `<Select>` option label that cannot
 * render JSX): the full formal name with the preferred name appended in parens
 * when {@link getPreferredParenthetical} applies — e.g. `Robert Smith (Bob)`.
 */
export function formatFormalNameWithPreferred(
  firstName: string | null | undefined,
  lastName: string | null | undefined,
  preferredName: string | null | undefined,
  fallback = '',
): string {
  const formal = `${(firstName ?? '').trim()} ${(lastName ?? '').trim()}`.trim() || fallback;
  const preferred = getPreferredParenthetical(preferredName, firstName);
  return preferred ? `${formal} (${preferred})` : formal;
}
